import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { interactiveFloorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { Tooltip } from '@components/tooltip/Tooltip';
import { Icon } from '@components/icon/Icon';
import iconButtonStyles from '@components/iconButton/IconButton.scss';
import { type FloorPlanTableData } from '@shared/types/floorPlans';
import styles from './AddOrRemoveTables.scss';

interface Props {
  toggleFloorPlanTableId: (id: string) => void;
  selectedFloorPlanTableIds: string[];
  onSelectAll: (tableIdsForSelectedFloorPlan: string[]) => void;
  onClearSelection: (tableIdsForSelectedFloorPlan: string[]) => void;
}

export const AddOrRemoveTables = ({
  toggleFloorPlanTableId,
  selectedFloorPlanTableIds,
  onSelectAll,
  onClearSelection,
}: Props) => {
  const { floorPlans, selectedFloorPlan, setSelectedFloorPlanId } =
    useAdminFloorPlans();

  const handleFloorPlanTableOnClick = (floorPlanTable: FloorPlanTableData) => {
    toggleFloorPlanTableId(floorPlanTable.id);
  };

  const calculateIsHighlighted = (floorPlanTable: FloorPlanTableData) =>
    selectedFloorPlanTableIds.some(
      (highlightedTableId: string) => highlightedTableId === floorPlanTable.id,
    );

  const selectAll = () => {
    onSelectAll(selectedFloorPlan.floorPlanTables.map((table) => table.id));
  };

  const clearSelection = () => {
    onClearSelection(
      selectedFloorPlan.floorPlanTables.map((table) => table.id),
    );
  };

  if (!selectedFloorPlan) return;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <section>
          <button
            className={styles.selectAll}
            onClick={selectAll}
            type="button"
          >
            <span>Select All of These Tables</span>
          </button>
        </section>
        <Tooltip
          content="Clear selection for tables on this floor plan"
          idPrefix="re-event-tooltip"
          tooltipClassName={iconButtonStyles.iconTooltip}
        >
          <button
            className={styles.clearSelection}
            onClick={clearSelection}
            type="button"
          >
            <Icon name="rotateCcw" ariaLabel="clear selection" />
            Clear selection
          </button>
        </Tooltip>
      </div>
      <div className={styles.floorPlan}>
        <div className={styles.floorPlanSelect}>
          <FloorPlanSelector
            floorPlans={floorPlans}
            selectedFloorPlan={selectedFloorPlan}
            onChange={(selectedFloorPlanId) => {
              const newFloorPlan = floorPlans.find(
                (fp) => fp.id === selectedFloorPlanId,
              );
              if (newFloorPlan) {
                setSelectedFloorPlanId(newFloorPlan.id);
              }
            }}
          />
        </div>

        <FloorPlan
          backgroundSrc={selectedFloorPlan.backgroundSrc}
          floorPlanTablesRenderer={interactiveFloorPlanTablesRendererFactory({
            tables: selectedFloorPlan.floorPlanTables,
            calculateIsHighlighted,
            calculateIsSelectable: () => true,
            handleTableOnClick: handleFloorPlanTableOnClick,
          })}
        />
      </div>
    </div>
  );
};
